import React from 'react'
import FlashBannerMessage from '@components/FlashBanner/shared/FlashBannerMessage'
import FlashBannerDiscount from '@components/FlashBanner/shared/FlashBannerDiscount'
import FlashBannerLink from '@components/FlashBanner/shared/FlashBannerLink'
import FlashBannerItemWrapper from '@components/FlashBanner/shared/FlashBannerItemWrapper'
import FlashBannerMessageWrapper from '@components/FlashBanner/shared/FlashBannerMessageWrapper'
import FlashBannerButton from '../shared/FlashBannerButton'
import { FlashBannerItem } from 'types/contentful/graphQlApi'
import { EmptyObject } from 'types/helpers'
import { useMediaQuery } from '@helpers/hooks'
import { BREAKPOINT_SIZES } from '@consts/index'
import { analytics } from '@helpers/analytics'
import { getLink } from '@helpers/env'

export type PromoBannerProps = Omit<
  FlashBannerItem,
  '__typename' | 'bannerName' | 'showBanner'
> & {
  bannerName?: string
  onClick?: () => void
  showIconMobile?: boolean
}

const PromoBanner = ({
  bannerName,
  messageDesktop,
  messageMobile,
  contentColor = 'white',
  discount,
  discountLabel,
  linkTextDesktop,
  linkUrl,
  backgroundColor,
  onClick
}: PromoBannerProps | EmptyObject) => {
  const isPhabletDown = useMediaQuery({
    maxWidth: BREAKPOINT_SIZES.TABLET - 1
  })
  const messageMobileTrimmed = messageMobile?.trim() || ''
  const messageDesktopTrimmed = messageDesktop?.trim() || ''
  const isLargeMobileText =
    isPhabletDown && messageMobileTrimmed.length >= 39 && discount
  const normalizedContentColor = contentColor ?? 'white'

  const handleBannerClick = () => {
    analytics('Click', {
      category: 'Flash Banner',
      label: linkTextDesktop ?? undefined,
      banner_name: bannerName,
      link_url: linkUrl ? getLink(linkUrl) : undefined
    })
    onClick?.()
  }

  // For a link banner the whole wrapper is the anchor on mobile (so it owns the
  // click) while the inner FlashBannerLink anchor owns it on desktop — this
  // fires the event once per navigational click. For a button banner (no
  // linkUrl) the wrapper carries the onClick. A message-only banner tracks
  // nothing, as there is no CTA to measure.
  const wrapperHandlesClick = linkUrl ? isPhabletDown : !!onClick

  return (
    <FlashBannerItemWrapper
      data-testid="PromoBanner"
      link={linkUrl}
      backgroundColor={backgroundColor}
      contentColor={normalizedContentColor}
      onClick={wrapperHandlesClick ? handleBannerClick : undefined}
      size={`${isLargeMobileText ? 'large' : 'regular'}`}
    >
      <FlashBannerMessageWrapper center={!linkUrl}>
        <FlashBannerMessage
          messageDesktop={messageDesktopTrimmed}
          messageMobile={messageMobileTrimmed}
        />
        {!!discount && !!discountLabel && (
          <FlashBannerDiscount
            discountLabel={discountLabel}
            discount={discount}
            center={!!linkUrl}
          />
        )}
      </FlashBannerMessageWrapper>
      {!!linkUrl && !!linkTextDesktop && (
        <FlashBannerLink
          linkUrl={linkUrl}
          linkTextDesktop={linkTextDesktop}
          onLinkClick={handleBannerClick}
        />
      )}
      {!linkUrl && typeof onClick === 'function' && !!linkTextDesktop && (
        <FlashBannerButton linkTextDesktop={linkTextDesktop} />
      )}
    </FlashBannerItemWrapper>
  )
}

export default PromoBanner
